import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../../../Experience'
import vertexShader from '../Shader/godRays/vertex.glsl'
import fragmentShader from '../Shader/godRays/fragment.glsl'
import streakVertexShader from '../Shader/emberStreaks/vertex.glsl'
import streakFragmentShader from '../Shader/emberStreaks/fragment.glsl'

/**
 * GodRayHalo — 強い光の惑星がトリガーゾーンに入った時のエフェクト2層。
 *  1. 放射状ゴッドレイ: ペルリンノイズで不規則にうねるスポーク＋強いコアグロー
 *  2. ダイアゴナルストリーク: 天体を光源に、画面全体へ下左方向に伸びる
 *     ほぼ平行な細い光条の束（Edgerunners の斜めレイ風）
 * どちらも天体のワールド座標に追従するビルボード。各1ドローコール。
 *
 * エフェクト共通インターフェース: fadeIn / fadeOut / updatePosition / update / dispose
 */
export default class GodRayHalo
{
  constructor(bodyConfig)
  {
    this.experience = new Experience()
    this.scene = this.experience.scene
    this.resources = this.experience.resources
    this.quality = this.experience.quality

    // Smoke と同じペルリンテクスチャを流用（角度方向に繰り返すので Repeat 必須）
    const perlinTexture = this.resources.items.perlinTexture0
    perlinTexture.wrapS = THREE.RepeatWrapping
    perlinTexture.wrapT = THREE.RepeatWrapping

    const geometry = new THREE.PlaneGeometry(24, 24)

    this.material = new THREE.ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(new THREE.Color(bodyConfig.haloColor)),
        uIntensity: new THREE.Uniform(0),
        uTime: new THREE.Uniform(0),
        uRayCount: new THREE.Uniform(14),
        uSharpness: new THREE.Uniform(3.0),
        uPerlinTexture: new THREE.Uniform(perlinTexture),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false, // 天体の球体にコア部分が削られないように
    })

    this.mesh = new THREE.Mesh(geometry, this.material)
    this.mesh.renderOrder = 2 // ハロー(1)より後に加算描画
    this.mesh.visible = false
    this.scene.add(this.mesh)

    this.setStreaks(perlinTexture, bodyConfig)
  }

  // 画面全体へ斜めに伸びる光条レイヤー
  setStreaks(perlinTexture, bodyConfig)
  {
    // 天体位置（カメラから約50ユニット）で画面全体を覆えるサイズ
    const width = 120
    const height = 70
    const geometry = new THREE.PlaneGeometry(width, height)

    this.streakMaterial = new THREE.ShaderMaterial({
      vertexShader: streakVertexShader,
      fragmentShader: streakFragmentShader,
      uniforms: {
        uColor: new THREE.Uniform(new THREE.Color(bodyConfig.haloColor)),
        uCoreColor: new THREE.Uniform(new THREE.Color('#fff3e2')),
        uIntensity: new THREE.Uniform(0),
        uTime: new THREE.Uniform(0),
        uAspect: new THREE.Uniform(width / height),
        uRayCount: new THREE.Uniform(42),
        uSharpness: new THREE.Uniform(3.5),
        uPerlinTexture: new THREE.Uniform(perlinTexture),
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false, // シーン全体の上にレンズフレアとして重ねる
    })

    this.streakMesh = new THREE.Mesh(geometry, this.streakMaterial)
    this.streakMesh.renderOrder = 3 // ゴッドレイ(2)より後に加算描画
    this.streakMesh.visible = false
    this.scene.add(this.streakMesh)
  }

  fadeIn()
  {
    this.mesh.visible = true
    this.streakMesh.visible = true
    for (const material of [this.material, this.streakMaterial]) {
      gsap.to(material.uniforms.uIntensity, {
        value: 1.0,
        duration: Math.max(2.5 * this.quality.motionScale, 0.01),
        ease: 'power2.inOut',
        overwrite: true,
      })
    }
  }

  fadeOut()
  {
    gsap.to(this.material.uniforms.uIntensity, {
      value: 0,
      duration: Math.max(2.5 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onComplete: () => { this.mesh.visible = false },
    })
    gsap.to(this.streakMaterial.uniforms.uIntensity, {
      value: 0,
      duration: Math.max(2.5 * this.quality.motionScale, 0.01),
      ease: 'power2.inOut',
      overwrite: true,
      onComplete: () => { this.streakMesh.visible = false },
    })
  }

  updatePosition(worldPosition)
  {
    this.mesh.position.copy(worldPosition)
    this.streakMesh.position.copy(worldPosition)
  }

  update(elapsed, cameraQuaternion)
  {
    if (!this.mesh.visible) return

    this.material.uniforms.uTime.value = elapsed
    this.mesh.quaternion.copy(cameraQuaternion)

    this.streakMaterial.uniforms.uTime.value = elapsed
    this.streakMesh.quaternion.copy(cameraQuaternion)
  }

  dispose()
  {
    this.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.material.dispose()

    this.scene.remove(this.streakMesh)
    this.streakMesh.geometry.dispose()
    this.streakMaterial.dispose()
  }
}
